import { Breadcrumbs } from '@mui/material';
import { AxiosResponse } from 'axios';
import { MprcContainer, MprcSpinnerIndicator } from 'components';
import { MprcFilterCount } from 'components/filter';
import { MprcBreadcrumbLink } from 'components/MprcBreadcrumbLink';
import { GetGeneralUnitsHierarchicalPayload, TrainingIndexUnitsHierarchicalResponse, TrainingIndexUnitTreeItemDto } from 'generated/CoreApi_v1';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashFooterExport } from 'views/dashboard/components/DashFooterExport';
import { TrainingIndexResult } from 'views/dashboard/TrainingIndex/components/TrainingIndexResult';
import { TrainingIndexUnitsFilter } from 'views/dashboard/TrainingIndex/components/TrainingIndexUnits';
import { TrainingIndexProps } from 'views/dashboard/TrainingIndex/TrainingIndexRoot';
import { UnitHierarchicalGridList, UnitHierarchicalItem } from 'views/foundation/unit/components/UnitHierarchicalGridList';

////////////////////////////////////////////////////////////////////////////

const SEARCH_RESULT = "SEARCH_RESULT";
const ROOT = "ROOT";

interface TrainingIndexUnitsHierarchicalListFilter extends TrainingIndexUnitsFilter {
    parentId: string | null;
}
interface IProps extends TrainingIndexProps {
    getApi: (data: GetGeneralUnitsHierarchicalPayload) => Promise<AxiosResponse<TrainingIndexUnitsHierarchicalResponse>>;
    filter: TrainingIndexUnitsFilter;
    onError: (e: any) => void;
    onDrillDownItem: (unitId: string, name: string) => void;
    onResetSearch: () => void;
    onExportData?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexUnitsHierarchicalList: React.FC<IProps> = ({ currentMonth, getApi, onError, onDrillDownItem, onExportData, onResetSearch, ...props }) => {

    const { t } = useTranslation("dashboard");

    const rootBreadcrumb = { unitId: ROOT, name: t("TrainingIndex.RootUnitText") } as TrainingIndexUnitTreeItemDto;
    const searchResultBreadcrumb = { unitId: SEARCH_RESULT, name: t("TrainingIndex.SearchResultText") } as TrainingIndexUnitTreeItemDto;

    const [loading, setLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<TrainingIndexUnitsHierarchicalResponse>();
    const [filter, setFilter] = useState<TrainingIndexUnitsHierarchicalListFilter>({ ...props.filter, parentId: null });
    const [breadcrumbsUnits, setBreadcrumpsUnits] = useState<TrainingIndexUnitTreeItemDto[]>([rootBreadcrumb]);

    const filtered = filter.searchText.length > 0;

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        getApi({
            yearMonth: currentMonth,
            searchText: filter.searchText,
            orderBy: filter.orderBy,
            parentId: filter.parentId,
        })
            .then(response => {
                setViewData(response.data);
                setBreadcrumpsUnits([filter.searchText ? searchResultBreadcrumb : rootBreadcrumb, ...(response.data?.treeUp ?? [])]);
            })
            .catch(e => onError(e))
            .finally(() => setLoading(false));
    }, [filter, currentMonth]);

    useEffect(() => {
        setFilter({ ...props.filter, parentId: null });
    }, [props.filter]);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleFilter = (_filter: Partial<TrainingIndexUnitsHierarchicalListFilter>) => {
        setFilter(state => ({ ...state, ..._filter }));
    };

    ///
    const handleClickBreadcrumbItem = (item: TrainingIndexUnitTreeItemDto) => {
        if (item.unitId != filter.parentId || (item.unitId == SEARCH_RESULT && filter.searchText != null) || (item.unitId == ROOT && breadcrumbsUnits.length > 1)) {
            if (item.unitId == ROOT || item.unitId == SEARCH_RESULT) {
                handleFilter({ parentId: null });
            }
            else {
                handleFilter({ parentId: item.unitId });
            }
        }
    };

    ///
    const handleClickItem = (item: UnitHierarchicalItem) => {
        const unit = viewData?.items.find(p => p.unitId == item.unitId);
        if (unit && unit.hasChildren) {
            handleFilter({ parentId: item.unitId });
        }
    };

    return (
        <>
            {!loading && (viewData?.items ?? []).length > 0 &&
                <MprcContainer margin="dense">
                    <Breadcrumbs aria-label="breadcrumb" color="primary">
                        {
                            breadcrumbsUnits.map((item, index) =>
                                <MprcBreadcrumbLink
                                    key={item.unitId}
                                    text={item.name}
                                    onClick={() => handleClickBreadcrumbItem(item)}
                                />
                            )
                        }
                    </Breadcrumbs>
                </MprcContainer>
            }
            {loading ?
                <MprcSpinnerIndicator />
                :
                viewData &&
                <>
                    {viewData.items.length == 0 &&
                        <MprcContainer>
                            <MprcFilterCount
                                loading={loading}
                                isEmpty={viewData.items.length == 0}
                                countText={t("TrainingIndex.UnitsRecordsFound", { count: viewData.items.length })}
                                filter={{
                                    active: filtered,
                                    text: t("TrainingIndex.UnitsRecordsFiltered"),
                                    onResetFilter: onResetSearch
                                }}

                            />
                        </MprcContainer>
                    }
                    <MprcContainer>
                        <UnitHierarchicalGridList
                            items={viewData.items.map(item => ({
                                ...item,
                                secondaryAction: item.trainingIndex != null ? <TrainingIndexResult value={item.trainingIndex} onClick={() => onDrillDownItem(item.unitId, item.name)} /> : ""

                            }))}
                            justifyLineItems="space-between"
                            onTitleClick={(item) => onDrillDownItem(item.unitId, item.name)}
                            onIconClickItem={handleClickItem}
                            highlightedItems={viewData.items.filter(p => p.isDeleted).map(p => p.unitId)}
                        />
                    </MprcContainer>
                    {onExportData && <DashFooterExport onExportData={onExportData} />}
                </>
            }
        </>
    );
};
